import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';
import { Link } from 'react-router-dom';
import { useSEO, SITE_URL } from '../../hooks/useSEO';

const RiskAssessmentPage = () => {
  useSEO({
    title: 'Cybersecurity Risk Assessment for Mortgage Companies | Mortgage Moat',
    description: 'Comprehensive cybersecurity risk assessment for mortgage firms. Identify vulnerabilities, ensure GLBA & FFIEC compliance, and get a prioritized remediation plan — delivered in 7 days for $250.',
    keywords: 'cybersecurity risk assessment, mortgage compliance, GLBA FFIEC audit, vulnerability assessment, financial firm security',
    canonical: '/risk-assessment',
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'Service',
        name: 'Cybersecurity Risk Assessment',
        provider: { '@type': 'Organization', name: 'Mortgage Moat', url: SITE_URL },
        url: `${SITE_URL}/risk-assessment`,
        description: 'Comprehensive security evaluation identifying vulnerabilities and ensuring GLBA and FFIEC regulatory compliance for mortgage companies.',
        offers: {
          '@type': 'Offer',
          price: '250',
          priceCurrency: 'USD',
        },
        areaServed: { '@type': 'Country', name: 'United States' },
        serviceType: 'Cybersecurity Assessment',
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/` },
          { '@type': 'ListItem', position: 2, name: 'Risk Assessment', item: `${SITE_URL}/risk-assessment` },
        ],
      },
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: [
          {
            '@type': 'Question',
            name: 'How long does the cybersecurity risk assessment take?',
            acceptedAnswer: { '@type': 'Answer', text: 'Most assessments are completed within 7 days or less from start to finish. The timeline depends on your company size and the complexity of your systems.' },
          },
          {
            '@type': 'Question',
            name: 'Will the risk assessment disrupt our daily operations?',
            acceptedAnswer: { '@type': 'Answer', text: 'No. Our assessment process is designed to minimize disruption. Most of our work happens behind the scenes, and we schedule any necessary interviews at your convenience.' },
          },
          {
            '@type': 'Question',
            name: 'Is this assessment sufficient for regulatory compliance?',
            acceptedAnswer: { '@type': 'Answer', text: 'Yes. Our assessments are designed to meet GLBA, FFIEC, and other regulatory requirements for mortgage companies. We provide documentation suitable for examiner review.' },
          },
        ],
      },
    ],
  });

  const features = [
    {
      icon: 'ri-shield-check-line',
      title: 'Comprehensive Security Audit',
      description: 'Complete evaluation of your current security posture, identifying vulnerabilities across all systems and processes'
    },
    {
      icon: 'ri-file-list-3-line',
      title: 'Detailed Risk Report',
      description: 'In-depth analysis with prioritized findings, risk scores, and clear documentation of all security gaps'
    },
    {
      icon: 'ri-roadmap-line',
      title: 'Actionable Remediation Plan',
      description: 'Step-by-step guidance to address vulnerabilities, with timelines and resource requirements'
    },
    {
      icon: 'ri-checkbox-circle-line',
      title: 'Compliance Verification',
      description: 'Ensure you meet regulatory requirements including GLBA, FFIEC, and industry standards'
    },
    {
      icon: 'ri-team-line',
      title: 'Schedule a Review',
      description: 'Direct access to cybersecurity professionals who understand mortgage industry requirements'
    },
    {
      icon: 'ri-refresh-line',
      title: 'Ongoing Support',
      description: 'Follow-up assessments and continuous guidance as you implement security improvements'
    }
  ];

  const benefits = [
    'Pass regulatory examinations with confidence',
    'Identify vulnerabilities before hackers do',
    'Reduce cyber insurance premiums',
    'Protect customer data and company reputation',
    'Meet GLBA and FFIEC requirements',
    'Get board-ready security documentation'
  ];

  const process = [
    {
      step: '1',
      title: 'Initial Consultation',
      description: 'We discuss your business, current security measures, and compliance requirements'
    },
    {
      step: '2',
      title: 'Comprehensive Assessment',
      description: 'Our experts evaluate your systems, policies, procedures, and employee practices'
    },
    {
      step: '3',
      title: 'Risk Analysis',
      description: 'We identify vulnerabilities, assess risk levels, and prioritize remediation needs'
    },
    {
      step: '4',
      title: 'Detailed Report Delivery',
      description: 'Receive a comprehensive report with findings, risk scores, and compliance status'
    },
    {
      step: '5',
      title: 'Action Plan Review',
      description: 'We walk you through the remediation plan and answer all your questions'
    },
    {
      step: '6',
      title: 'Implementation Support',
      description: 'Ongoing guidance as you address vulnerabilities and strengthen your security'
    }
  ];

  return (
    <div className="min-h-screen">
      <Header />
      
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-red-600 to-red-800 text-white py-24">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="relative max-w-7xl mx-auto px-6">
          <div className="max-w-3xl">
            <div className="inline-block bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full text-sm font-bold mb-6">
              CYBERSECURITY RISK ASSESSMENT
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Upcoming Exam? Know Your Vulnerabilities Before the Auditors Do
            </h1>
            <p className="text-xl mb-8 text-white/90">
              Get a comprehensive security evaluation that identifies risks, ensures compliance, and provides a clear roadmap to protect your mortgage business.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="/purchase/risk-assessment"
                className="inline-flex items-center justify-center gap-2 bg-cyan-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-cyan-700 transition-colors whitespace-nowrap cursor-pointer"
              >
                Get Started
                <i className="ri-arrow-right-line"></i>
              </a>
              <a 
                href="/schedule-call"
                className="inline-flex items-center justify-center gap-2 bg-white text-cyan-600 px-8 py-4 rounded-lg font-semibold hover:bg-cyan-50 border-2 border-cyan-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                Schedule Consultation
                <i className="ri-calendar-line"></i>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Why You Need This */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Why Mortgage Companies Need Risk Assessments
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Regulatory examiners expect you to know your security posture. Don't wait for an audit to discover your vulnerabilities.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <div key={index} className="flex items-start gap-4 bg-gray-50 p-6 rounded-xl">
                <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                  <i className="ri-checkbox-circle-fill text-green-500 text-2xl"></i>
                </div>
                <p className="text-lg font-semibold text-gray-800">{benefit}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              What's Included in Your Assessment
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              A thorough evaluation of your entire security program, delivered by experts who understand mortgage industry requirements.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow">
                <div className="w-16 h-16 flex items-center justify-center bg-red-100 rounded-xl mb-6">
                  <i className={`${feature.icon} text-3xl text-red-600`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Our Assessment Process
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              A proven methodology that delivers results in weeks, not months.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {process.map((item, index) => (
              <div key={index} className="relative">
                <div className="flex items-start gap-4">
                  <div className="w-16 h-16 flex items-center justify-center flex-shrink-0 bg-red-600 text-white text-2xl font-bold rounded-full">
                    {item.step}
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{item.title}</h3>
                    <p className="text-gray-600">{item.description}</p>
                  </div>
                </div>
                {index < process.length - 1 && (
                  <div className="hidden lg:block absolute top-8 left-full w-full h-0.5 bg-gray-200 -ml-4"></div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-6">
          <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
            <div className="bg-red-600 text-white text-center py-6">
              <div className="text-sm font-bold uppercase tracking-wide mb-2">Special Pricing</div>
              <div className="text-5xl font-bold mb-2">$250</div>
              <div className="text-xl">
                <span className="line-through opacity-75">$499</span>
                <span className="ml-3 bg-white/20 px-3 py-1 rounded-full text-sm font-bold">Save $249</span>
              </div>
            </div>
            
            <div className="p-8 md:p-12">
              <h3 className="text-3xl font-bold text-gray-900 mb-6 text-center">
                Complete Risk Assessment Package
              </h3>
              
              <ul className="space-y-4 mb-8">
                {[
                  'Full security infrastructure audit',
                  'Policy and procedure review',
                  'Employee security practice evaluation',
                  'Vendor risk assessment',
                  'Compliance gap analysis (GLBA, FFIEC)',
                  'Detailed risk report with scores',
                  'Prioritized remediation roadmap',
                  'Executive summary for board presentation',
                  '90-minute consultation call',
                  '30 days of follow-up support'
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                      <i className="ri-check-line text-green-500 text-xl"></i>
                    </div>
                    <span className="text-gray-700 text-lg">{item}</span>
                  </li>
                ))}
              </ul>

              <div className="text-center">
                <Button variant="primary" size="lg" href="/purchase/risk-assessment" className="px-12 py-4 font-bold whitespace-nowrap text-lg">
                  Get Assessment
                </Button>
                <p className="text-sm text-gray-500 mt-4">
                  Results delivered in 7 days or less • 100% satisfaction guarantee
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">
            Frequently Asked Questions
          </h2>
          
          <div className="space-y-6">
            {[
              {
                question: 'How long does the assessment take?',
                answer: 'Most assessments are completed within 7 days or less from start to finish. The timeline depends on your company size and the complexity of your systems.'
              },
              {
                question: 'Will this disrupt our daily operations?',
                answer: 'No. Our assessment process is designed to minimize disruption. Most of our work happens behind the scenes, and we schedule any necessary interviews at your convenience.'
              },
              {
                question: 'What if we find critical vulnerabilities?',
                answer: 'We provide immediate notification of any critical risks discovered during the assessment, along with emergency remediation guidance. Your full report will include a prioritized action plan.'
              },
              {
                question: 'Is this assessment sufficient for regulatory compliance?',
                answer: 'Yes. Our assessments are designed to meet GLBA, FFIEC, and other regulatory requirements for mortgage companies. We provide documentation suitable for examiner review.'
              },
              {
                question: 'Do you offer ongoing assessments?',
                answer: 'Absolutely. We recommend annual assessments to maintain compliance and security. We offer discounted rates for recurring assessments and can bundle with our managed services.'
              }
            ].map((faq, index) => (
              <div key={index} className="bg-gray-50 rounded-xl p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-3">{faq.question}</h3>
                <p className="text-gray-600">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-br from-red-600 to-red-800 text-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Don't Wait for an Audit to Find Your Vulnerabilities
          </h2>
          <p className="text-xl mb-8 text-white/90">
            Get ahead of regulatory requirements and protect your business with a comprehensive risk assessment.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="/purchase/risk-assessment"
              className="inline-flex items-center justify-center gap-2 bg-cyan-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-cyan-700 transition-colors whitespace-nowrap cursor-pointer"
            >
              Request Assessment Now
              <i className="ri-arrow-right-line"></i>
            </a>
            <a 
              href="/schedule-call"
              className="inline-flex items-center justify-center gap-2 bg-white text-cyan-600 px-8 py-4 rounded-lg font-semibold hover:bg-cyan-50 border-2 border-cyan-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              Talk to an Expert
              <i className="ri-user-voice-line"></i>
            </a>
          </div>
          <p className="text-sm text-white/75 mt-6">
            Questions? Call us at (555) 123-4567 or email info@mortgagemoat.com
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default RiskAssessmentPage;